import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { connect } from 'react-redux';
import { AlertMessage } from '../../../components/AlertMessage/component';
import {
  selectCurrentCourseFacialCheckEnabled,
  selectCurrentCourseKeystrokeEnabled,
} from '../../../../ReduxStorage/Courses/selectors';
import { setIdentitySessionSettings } from '../../../../ReduxStorage/Courses/dispatchers';
import { ReduxState } from '../../../../ReduxStorage/state';
import { InputLabel } from '../../../components/InputLabel/component';
import { AutoSaveState } from '../../../components/AutoSaveIndicator/component';
import {
  ContentWrapper,
  LabelHeading,
  InputWrapper,
  LabelDescription,
} from './styles';
import { selectCurrentCourseLinkedPortalIsEnabled } from '../../../../ReduxStorage/Portals/selectors';
import { Link } from '../../Link';
import { ROUTE_SSO } from '../../../../ReduxStorage/routeActions';
import { HELP_ORIGIN } from '../../../../constants';

interface CourseIdentityIntegrationSettingsProps {
  facialCheckEnabled: boolean;
  keystrokeEnabled: boolean;
  identitySessionEnabled?: boolean;
  setIdentitySessionSettings: ({
    keystrokeEnabled,
    facialCheckEnabled,
  }: {
    keystrokeEnabled: boolean;
    facialCheckEnabled: boolean;
  }) => Promise<void>;
}

const CourseIdentityIntegrationSettings: React.FC<CourseIdentityIntegrationSettingsProps> = ({
  facialCheckEnabled,
  keystrokeEnabled,
  identitySessionEnabled,
  setIdentitySessionSettings,
}) => {
  const [formState, setFormState] = useState({
    keystrokeEnabled: false,
    facialCheckEnabled: false,
  });

  const [autoSaveStates, setAutoSaveStates] = useState({
    keystrokeEnabled: AutoSaveState.Normal,
    facialCheckEnabled: AutoSaveState.Normal,
  });

  const updateIdentitySetting = (
    type: 'keystrokeEnabled' | 'facialCheckEnabled',
    checked: boolean
  ): void => {
    setAutoSaveStates((prev) => ({
      ...prev,
      [type]: AutoSaveState.Saving,
    }));
    setIdentitySessionSettings({ ...formState, [type]: checked }).then(() => {
      setAutoSaveStates((prev) => ({
        ...prev,
        [type]: AutoSaveState.Saved,
      }));
    });
  };

  useEffect(() => {
    setFormState({
      facialCheckEnabled,
      keystrokeEnabled,
    });
  }, [facialCheckEnabled, keystrokeEnabled]);

  if (!identitySessionEnabled) {
    return null;
  }

  return (
    <section className="bordered">
      <h3>Know Your Learner</h3>
      <ContentWrapper>
        <AlertMessage
          kind="info"
          message={
            <div>
              <p>
                When enabled, the information captured by these integration
                tools will be displayed on all learner posts made via the
                relevant widgets, as well as on all learner comments. It will
                only be visible to course admins, course staff and the learner
                making the post or comment.
              </p>
              <Link
                to={{
                  type: ROUTE_SSO,
                  params: {
                    action: 'help',
                  },
                  query: {
                    url: `${HELP_ORIGIN}/t/83hl619`,
                  },
                }}
                target="_blank"
              >
                Learn more about identity verification on OpenLearning
              </Link>
            </div>
          }
          inContent
        />
        <LabelHeading>Keystroke check</LabelHeading>
        <InputWrapper>
          <input
            type="checkbox"
            className="identity-session-params"
            checked={formState.keystrokeEnabled}
            name="keystroke-checkbox"
            id="keystroke-checkbox"
            onChange={(event) => {
              updateIdentitySetting('keystrokeEnabled', event.target?.checked);
            }}
          />
          <div>
            <InputLabel
              htmlFor="keystroke-checkbox"
              autoSave
              autoSaveState={autoSaveStates.keystrokeEnabled}
            >
              Enable keystroke check for this course
            </InputLabel>
            <LabelDescription>
              This will record and display keystroke check information across
              comments and posts made via widgets such as Post text and Share
              anything.
            </LabelDescription>
          </div>
        </InputWrapper>

        <LabelHeading>Facial identity check</LabelHeading>
        <InputWrapper>
          <input
            type="checkbox"
            className="identity-session-params"
            checked={formState.facialCheckEnabled}
            name="facialCheck-checkbox"
            id="facialCheck-checkbox"
            onChange={(event) => {
              updateIdentitySetting(
                'facialCheckEnabled',
                event.target?.checked
              );
            }}
          />
          <div>
            <InputLabel
              htmlFor="facialCheck-checkbox"
              autoSave
              autoSaveState={autoSaveStates.facialCheckEnabled}
            >
              Enable facial identity check for this course
            </InputLabel>
            <LabelDescription>
              This will record and store short videos of learners’ faces
              throughout this course. Facial capture status will be displayed on
              all learner comments and posts made via widgets such as Post text,
              Post file, Share anything.
            </LabelDescription>
          </div>
        </InputWrapper>
      </ContentWrapper>
    </section>
  );
};

export const ConnectedCourseIdentityIntegrationSettings = connect(
  (state: ReduxState) => ({
    facialCheckEnabled: selectCurrentCourseFacialCheckEnabled(state),
    keystrokeEnabled: selectCurrentCourseKeystrokeEnabled(state),
    identitySessionEnabled: selectCurrentCourseLinkedPortalIsEnabled(state),
  }),
  {
    setIdentitySessionSettings,
  }
)(CourseIdentityIntegrationSettings);

export const MountableCourseIdentityIntegrationSettings = (): React.ReactPortal | null => {
  const elem = document.getElementById('identity-session-integrations-mount');
  if (elem) {
    return ReactDOM.createPortal(
      <ConnectedCourseIdentityIntegrationSettings />,
      elem
    );
  }

  return null;
};
